import React from 'react'
import TextInput from '../../../../components/atoms/TextInput/TextInput'
import Button from '../../../../components/atoms/Button/Button'
import SelectInput from '../../../../components/atoms/SelectInput/SelectInput'
import { SpendingDemandTypes } from '../../types/spendingDemand.types'
import { InputAdornment, CircularProgress } from '@mui/material'
import { CheckValidationIcon } from '../../../../assets/JWayIcons'



interface ProjectOption {
  id: string | number;
  name: string;
}

interface BoxIdValidationStatus {
  isValid: boolean;
  isLoading: boolean;
  isError: boolean;
}

interface NewSpendingDemandFormViewProps {
  description: string;
  grossAmount: number;
  paymentDate: string; // ISO string
  category: string;
  isAlreadyPaid: boolean;
  project?: ProjectOption | undefined;
  projectOptions?: ProjectOption[];
  spendingDemandType?: SpendingDemandTypes;
  boxId?: string;
  boxIdValidationStatus?: BoxIdValidationStatus;
  onChange: (key: string, value: any) => void;
  onSubmit: (e?: React.FormEvent) => void;
  errorMessage?: string | null;
  projectPickerRender?: () => JSX.Element
  isUpdate?: boolean
  FilePickerRender?: () => JSX.Element
}

// Loading View Component
export function LoadingView() {
  return (
    <div style={{
      maxWidth: '400px',
      margin: '0 auto',
      padding: '40px 20px',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      gap: '24px',
      textAlign: 'center'
    }}>
      <div style={{
        width: '80px',
        height: '80px',
        borderRadius: '50%',
        backgroundColor: '#e3f2fd',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        marginBottom: '16px'
      }}>
        <div style={{
          width: '40px',
          height: '40px',
          border: '4px solid #bbdefb',
          borderTop: '4px solid #2196f3',
          borderRadius: '50%',
          animation: 'spin 1s linear infinite'
        }} />
      </div>

      <div>
        <h1 style={{
          fontSize: '24px',
          fontWeight: '600',
          color: '#1976d2',
          margin: '0 0 12px 0'
        }}>
          Chargement...
        </h1>
        
        <p style={{
          fontSize: '16px',
          color: '#666',
          margin: '0',
          lineHeight: '1.5'
        }}>
          Chargement de la demande de dépense...
        </p>
      </div>

      <style>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
}

// Error View Component
export function ErrorView({ message = "Une erreur est survenue lors du chargement des données.", onRetry }: { 
  message?: string;
  onRetry?: () => void;
}) {
  return (
    <div style={{
      maxWidth: '400px',
      margin: '0 auto',
      padding: '40px 20px',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      gap: '24px',
      textAlign: 'center'
    }}>
      <div style={{
        width: '80px',
        height: '80px',
        borderRadius: '50%',
        backgroundColor: '#f44336',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        marginBottom: '16px'
      }}>
        <svg 
          width="40" 
          height="40" 
          viewBox="0 0 24 24" 
          fill="none" 
          stroke="white" 
          strokeWidth="2"
        >
          <circle cx="12" cy="12" r="10"></circle>
          <line x1="15" y1="9" x2="9" y2="15"></line>
          <line x1="9" y1="9" x2="15" y2="15"></line>
        </svg>
      </div>

      <div>
        <h1 style={{
          fontSize: '24px',
          fontWeight: '600',
          color: '#d32f2f',
          margin: '0 0 12px 0'
        }}>
          Erreur
        </h1>
        
        <p style={{
          fontSize: '16px',
          color: '#666',
          margin: '0',
          lineHeight: '1.5'
        }}>
          {message}
        </p>
      </div>

      <div style={{
        width: '100%',
        padding: '16px',
        backgroundColor: '#ffebee',
        borderRadius: '8px',
        border: '1px solid #ffcdd2'
      }}>
        <p style={{
          fontSize: '14px',
          color: '#d32f2f',
          margin: '0',
          fontWeight: '500'
        }}>
          ⚠ Veuillez réessayer ou contacter le support
        </p>
      </div>

      {onRetry && (
        <button
          onClick={onRetry}
          style={{
            backgroundColor: '#f44336',
            color: 'white',
            border: 'none',
            borderRadius: '8px',
            padding: '12px 24px',
            fontSize: '16px',
            fontWeight: '600',
            cursor: 'pointer',
            transition: 'background-color 0.2s'
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.backgroundColor = '#d32f2f';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.backgroundColor = '#f44336';
          }}
        >
          🔄 Réessayer
        </button>
      )}
    </div>
  );
}

// Success View Component
export function SuccessView({ isUpdate, onNewDemand }: { 
  isUpdate?: boolean;
  onNewDemand?: () => void;
}) {
  return (
    <div style={{
      maxWidth: '400px',
      margin: '0 auto',
      padding: '40px 20px',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      gap: '24px',
      textAlign: 'center'
    }}>
      <div style={{
        width: '80px',
        height: '80px',
        borderRadius: '50%',
        backgroundColor: '#4caf50',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        marginBottom: '16px'
      }}>
        <svg 
          width="40" 
          height="40" 
          viewBox="0 0 24 24" 
          fill="none" 
          stroke="white" 
          strokeWidth="2"
        >
          <polyline points="20,6 9,17 4,12"></polyline>
        </svg>
      </div>

      <div>
        <h1 style={{
          fontSize: '24px',
          fontWeight: '600',
          color: '#2e7d32',
          margin: '0 0 12px 0'
        }}>
          {isUpdate ? "Demande modifiée !" : "Demande créée !"}
        </h1>
        
        <p style={{
          fontSize: '16px',
          color: '#666',
          margin: '0',
          lineHeight: '1.5'
        }}>
          {isUpdate 
            ? "La demande de dépense a été modifiée avec succès." 
            : "La demande de dépense a été créée avec succès."
          }
        </p>
      </div>

      <div style={{
        width: '100%',
        padding: '16px',
        backgroundColor: '#f1f8e9',
        borderRadius: '8px',
        border: '1px solid #c8e6c9'
      }}>
        <p style={{
          fontSize: '14px',
          color: '#388e3c',
          margin: '0',
          fontWeight: '500'
        }}>
          ✓ Votre demande a été traitée avec succès
        </p>
      </div>

      {onNewDemand && !isUpdate && (
        <button
          onClick={onNewDemand}
          style={{
            backgroundColor: '#4caf50',
            color: 'white',
            border: 'none',
            borderRadius: '8px',
            padding: '12px 24px',
            fontSize: '16px',
            fontWeight: '600',
            cursor: 'pointer',
            transition: 'background-color 0.2s'
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.backgroundColor = '#388e3c';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.backgroundColor = '#4caf50';
          }}
        >
          ➕ Nouvelle demande
        </button>
      )}
    </div>
  );
}

// Not Found View Component
export function NotFoundView({ onRetry }: { onRetry?: () => void }) {
  return (
    <div style={{
      maxWidth: '400px',
      margin: '0 auto',
      padding: '40px 20px',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      gap: '24px',
      textAlign: 'center'
    }}>
      <div style={{
        width: '80px',
        height: '80px',
        borderRadius: '50%',
        backgroundColor: '#9e9e9e',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        marginBottom: '16px'
      }}>
        <svg 
          width="40" 
          height="40" 
          viewBox="0 0 24 24" 
          fill="none" 
          stroke="white" 
          strokeWidth="2"
        >
          <circle cx="11" cy="11" r="8"></circle>
          <path d="21 21l-4.35-4.35"></path>
          <line x1="11" y1="8" x2="11" y2="14"></line>
          <line x1="8" y1="11" x2="14" y2="11"></line>
        </svg>
      </div>

      <div>
        <h1 style={{
          fontSize: '24px',
          fontWeight: '600',
          color: '#616161',
          margin: '0 0 12px 0'
        }}>
          Demande non trouvée
        </h1>
        
        <p style={{
          fontSize: '16px',
          color: '#666',
          margin: '0',
          lineHeight: '1.5'
        }}>
          La demande de dépense demandée n'a pas été trouvée.
        </p>
      </div>

      <div style={{
        width: '100%',
        padding: '16px',
        backgroundColor: '#f5f5f5',
        borderRadius: '8px',
        border: '1px solid #e0e0e0'
      }}>
        <p style={{
          fontSize: '14px',
          color: '#616161',
          margin: '0',
          fontWeight: '500'
        }}>
          🔍 Vérifiez l'identifiant de la demande
        </p>
      </div>

      {onRetry && (
        <button
          onClick={onRetry}
          style={{
            backgroundColor: '#9e9e9e',
            color: 'white',
            border: 'none',
            borderRadius: '8px',
            padding: '12px 24px',
            fontSize: '16px',
            fontWeight: '600',
            cursor: 'pointer',
            transition: 'background-color 0.2s'
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.backgroundColor = '#757575';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.backgroundColor = '#9e9e9e';
          }}
        >
          🔄 Actualiser
        </button>
      )}
    </div>
  );
}

/*
const categories = [
  { value: '', label: 'Sélectionner une catégorie' },
  { value: 'Achat', label: 'Achat' },
  { value: 'Service', label: 'Service' },
  { value: 'Autre', label: 'Autre' },
];
*/

export default function NewSpendingDemandFormView({
  description,
  grossAmount,
  paymentDate,
  category,
  isAlreadyPaid,
  project,
  projectOptions = [],
  spendingDemandType,
  boxId,
  boxIdValidationStatus,
  onChange,
  onSubmit,
  errorMessage,
  projectPickerRender,
  isUpdate,
  FilePickerRender
}: NewSpendingDemandFormViewProps) {
  // Options pour le type de dépense
  const spendingDemandTypeOptions = [
    { value: '', label: 'Sélectionner un type de dépense' },
    { value: 'PROJECT', label: 'Projet' },
    { value: 'BOX', label: 'Box' },
    { value: 'CURRENT', label: 'Dépense courante' },
  ];

  return (
    <div style={{ 
      maxWidth: '400px', 
      margin: '0 auto', 
      padding: '20px',
      display: 'flex',
      flexDirection: 'column',
      gap: '20px'
    }}>
      <div style={{ marginBottom: '20px' }}>

        <p style={{ 
          margin: '0', 
          color: '#666', 
          fontSize: '14px',
          lineHeight: '1.5'
        }}>
          Veuillez remplir tous les champs requis pour {isUpdate ? 'modifier' : 'créer'} votre demande.
        </p>
      </div>

      {errorMessage && (
        <div style={{ 
          color: '#d32f2f', 
          backgroundColor: '#ffebee', 
          padding: '12px', 
          borderRadius: '4px',
          fontSize: '14px',
          border: '1px solid #ffcdd2'
        }}>
          {errorMessage}
        </div>
      )}

      <form style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
        <div>
          <TextInput
            label="Description (Explication de la demande)"
            value={description}
            onChange={e => onChange('description', e.target.value)}
            placeholder="Décrivez votre demande de dépense"
            multiline
            rows={4}
            style={{ marginBottom: '8px' }}
          />
        </div>

        <div>
          <TextInput
            label="Montant Prévisionnel (FCFA)"
            value={grossAmount}
            onChange={e => onChange('grossAmount', parseFloat(e.target.value) || 0)}
            type="number"
            placeholder="0"
            style={{ marginBottom: '8px' }}
          />
        </div>

        <div>
          <TextInput
            label="Date de paiement"
            value={paymentDate.slice(0, 10)}
            onChange={e => onChange('paymentDate', e.target.value)}
            type="date"
            style={{ marginBottom: '8px' }}
          />
        </div>

        <div>
          <SelectInput
            label="Type de dépense"
            value={spendingDemandType || ''}
            options={spendingDemandTypeOptions}
            handleChange={(value) => onChange('spendingDemandType', value)}
          />
        </div>

        {spendingDemandType === 'BOX' && (
          <div>
            <TextInput
              label="Box ID"
              value={boxId || ''}
              onChange={e => onChange('boxId', e.target.value)}
              placeholder="Entrez l'ID de la box"
              style={{ marginBottom: '8px' }}
              error={boxIdValidationStatus?.isError && !boxIdValidationStatus?.isLoading && boxId && !boxIdValidationStatus?.isValid ? true : false}
              InputProps={{
                endAdornment: boxId && boxIdValidationStatus ? (
                  <InputAdornment position="end">
                    {boxIdValidationStatus.isLoading ? (
                      <CircularProgress size={20} />
                    ) : boxIdValidationStatus.isValid ? (
                      <CheckValidationIcon size={20} color="#4caf50" />
                    ) : boxIdValidationStatus.isError ? (
                      <svg
                        width="20"
                        height="20"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="#f44336"
                        strokeWidth="2"
                      >
                        <circle cx="12" cy="12" r="10"></circle>
                        <line x1="15" y1="9" x2="9" y2="15"></line>
                        <line x1="9" y1="9" x2="15" y2="15"></line>
                      </svg>
                    ) : null}
                  </InputAdornment>
                ) : null,
              }}
            />
          </div>
        )}

        {spendingDemandType === 'PROJECT' && (
          <div>
            <label style={{ 
              display: 'block', 
              marginBottom: '8px', 
              fontSize: '14px', 
              fontWeight: '500', 
              color: '#333' 
            }}>
              Projet
            </label>
            {projectPickerRender && projectPickerRender()}
          </div>
        )}
        {FilePickerRender && FilePickerRender()}
        <div style={{ marginTop: '20px' }}>
          <Button
            label={isUpdate ? 'Modifier la demande' : 'Envoyer la demande'}
            onClick={() => onSubmit()} // L'événement onSubmit du form sera utilisé
            type="submit"
            variant="primary"
            height="48px"
          />
        </div>
      </form>
    </div>
  );
}


